import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2, MessageSquareHeart, RotateCcw, Send } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { downloadBlob } from "../../../lib/downloadBlob";
import { dayOf, plusDays } from "../../../lib/initialAssessmentAdmin";
import { MODES, countLine, modeWord, reopenLabel, reopenQuestion, sendLabel, sendQuestion } from "../../../lib/finalFeedbackAdmin";
import DeadlineDialog from "../assessment/DeadlineDialog";
import FeedbackResults from "../../FeedbackResults";

/**
 * A group's final feedback, on its sheet (30.10.2026): how the group gets it,
 * who answered and who is missing, sending and reopening, the results (the
 * team's, however few) and the Excel. Owner and admin FPED.
 */
export default function GroupFeedbackPanel({ groupId, groupName }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // "send" | "reopen"
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/groups/${groupId}/final-feedback`);
      setData(r.data);
      setMode(r.data.mode);
    } catch {
      setData({ failed: true });
    }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <section className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 className="animate-spin" size={14} /> Feedbackul de final…</section>;
  }
  if (data.failed) return null;

  const saveMode = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/groups/${groupId}/final-feedback`, { mode });
      toast.success("Am salvat cum primește grupa chestionarul de feedback.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva alegerea."));
    } finally {
      setBusy(false);
    }
  };

  const run = async (day) => {
    const kind = dialog;
    setBusy(true);
    try {
      const r = await api.post(`/admin/groups/${groupId}/final-feedback/${kind}`, { deadline: day });
      toast.success(kind === "send"
        ? `Am trimis chestionarul de feedback către ${r.data.sent} cursanți.`
        : `Am redeschis chestionarul de feedback pentru ${r.data.reopened} cursanți.`);
      setDialog(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut trimite chestionarul de feedback."));
    } finally {
      setBusy(false);
    }
  };

  const exportXlsx = () => downloadBlob(`/admin/groups/${groupId}/final-feedback/export`,
    `Feedback de final - ${groupName || groupId}.xlsx`, setExporting);

  const missing = data.missing || [];
  const toSend = missing.filter((m) => m.state !== "closed").length;
  const toReopen = missing.filter((m) => m.state === "closed").length;
  const results = data.results || {};

  return (
    <section className="rounded-2xl border border-[#E5E7EB] p-4" data-testid="group-feedback-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#965EA7]">
          <MessageSquareHeart size={14} /> Feedback de final:{" "}
          <span className="text-sm normal-case tracking-normal text-[#373A45]" data-testid="group-feedback-mode">{modeWord(data.mode)}</span>
        </h3>
        {results.answered > 0 && (
          <button type="button" onClick={exportXlsx} disabled={exporting}
            className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50">
            {exporting ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />} Excel
          </button>
        )}
      </div>

      <fieldset className="mt-3 grid gap-1.5" disabled={busy}>
        {MODES.map((m) => (
          <label key={m.key} className="flex items-start gap-2 text-sm">
            <input type="radio" name={`feedback-mode-${groupId}`} className="mt-1 accent-[#965EA7]"
              checked={mode === m.key} onChange={() => setMode(m.key)} />
            <span><strong className="text-[#373A45]">{m.label}:</strong> <span className="text-[#6B7280]">{m.hint}</span></span>
          </label>
        ))}
      </fieldset>
      {(mode !== data.mode || !data.chosen) && (
        <button type="button" onClick={saveMode} disabled={busy}
          className="mt-2 rounded-full bg-[#965EA7] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
          data-testid="group-feedback-save-mode">
          Salvează alegerea
        </button>
      )}

      {data.mode !== "external" && (
        <>
          <p className="mt-4 text-sm text-[#373A45]" data-testid="group-feedback-count">{countLine(data)}</p>
          {data.url && <p className="mt-1 text-xs text-[#6B7280]">Linkul formatorului: {data.url.replace(/^https?:\/\//, "")}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {!data.closed && toSend > 0 && (
              <button type="button" onClick={() => setDialog("send")}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986]"
                data-testid="group-feedback-send">
                <Send size={12} /> {sendLabel(toSend)}
              </button>
            )}
            {toReopen > 0 && (
              <button type="button" onClick={() => setDialog("reopen")}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#965EA7] px-3 py-1.5 text-xs font-semibold text-[#7A4986] hover:bg-[#F0E7F4]"
                data-testid="group-feedback-reopen">
                <RotateCcw size={12} /> {reopenLabel(toReopen)}
              </button>
            )}
          </div>
          {missing.length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer font-semibold text-[#373A45]">Cine n-a răspuns ({missing.length})</summary>
              <ul className="mt-2 grid gap-1">
                {missing.map((m) => (
                  <li key={m.enrollment_id}>
                    <Link to={`/admin/enrollments?e=${m.enrollment_id}`} className="text-[#373A45] hover:text-[#7A4986] hover:underline">
                      {m.name || "—"}
                    </Link>
                    {m.sent_at && <span className="ml-2 text-xs text-[#6B7280]">email trimis</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {results.shown && results.answered > 0 && (
        <div className="mt-5 border-t border-[#E5E7EB] pt-4"><FeedbackResults results={results} people={data.people} /></div>
      )}

      <DeadlineDialog open={dialog === "send"} title="Trimiți chestionarul de feedback?"
        question={(day) => sendQuestion(toSend, day || data.deadline)} initialDay={dayOf(data.deadline)} withDate={false}
        confirmLabel="Trimite" busy={busy} onConfirm={() => run(null)} onClose={() => setDialog(null)} />
      <DeadlineDialog open={dialog === "reopen"} title="Redeschizi chestionarul de feedback?"
        question={(day) => reopenQuestion(toReopen, day)} initialDay={plusDays(7)}
        confirmLabel="Redeschide și trimite" busy={busy} onConfirm={run} onClose={() => setDialog(null)} />
    </section>
  );
}
